import { ModelRowLabel } from "@/components/harness/agentRows";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { isCohesiveHarness } from "@/constants/agents";
import { AgentReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { cn } from "@/utils";
import { trackComposerControlChanged } from "@/utils/analytics";
import { HarnessCapabilityEffort } from "@cohesive/api-client";
import { Check, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { HarnessControl } from "../hooks/useHarnessControl";
import { MENU_CLASS, PILL_CLASS, ROW_CLASS } from "./menuStyles";
import { ReasoningEffortButton } from "./ReasoningEffortButton";

/** The Thinking scale's segment labels; `none` is OpenAI's, shown as off. */
const EFFORT_SHORT: Record<string, string> = {
  none: "Off",
  low: "Low",
  medium: "Med",
  high: "High",
  xhigh: "X-High",
  max: "Max",
};

function effortLabel(effort: HarnessCapabilityEffort | undefined) {
  if (!effort) return undefined;
  return effort.id === "none" ? "Off" : effort.label;
}

/**
 * The composer's right-hand dial. On Cohesive it is the effort tier — Cohesive never names a
 * model — and on a subscription the model, by alias, with its thinking effort.
 */
export function AgentEffortPill({
  control,
  tier,
  onTierChange,
}: {
  control: HarnessControl;
  tier: AgentReasoningEffort;
  onTierChange?: (tier: AgentReasoningEffort) => void;
}) {
  // A legacy session on a harness that is not an agent (opencode) still has models to switch.
  if (!isCohesiveHarness(control.harness) || control.models.length) {
    return control.models.length ? (
      <ModelThinkingButton control={control} />
    ) : null;
  }
  return (
    <ReasoningEffortButton
      effort={tier}
      onChange={(next) => onTierChange?.(next)}
      align="end"
      tiers={control.agentCapability?.tiers}
    />
  );
}

function ModelThinkingButton({ control }: { control: HarnessControl }) {
  const [open, setOpen] = useState(false);
  const [allModels, setAllModels] = useState(false);
  const { model, models, aliases, efforts, effort } = control;

  const alias = aliases.find((a) => a.model_id === model);
  const modelLabel =
    alias?.label ?? models.find((m) => m.id === model)?.label ?? model;
  const activeEffort = efforts.find((e) => e.id === effort);
  const label = [modelLabel, effortLabel(activeEffort)]
    .filter(Boolean)
    .join(" · ");
  const aliasModels = new Set(aliases.map((a) => a.model_id));
  const hasMoreModels = models.some((m) => !aliasModels.has(m.id));

  const pickModel = (next: string) => {
    if (next !== model) {
      trackComposerControlChanged({ control: "model", value: next });
    }
    control.setModel(next);
  };
  const pickEffort = (next: string) => {
    if (next !== effort) {
      trackComposerControlChanged({ control: "reasoning_effort", value: next });
    }
    control.setEffort(next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setAllModels(false);
      }}
    >
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className={PILL_CLASS}
              aria-label={`Model: ${label}`}
            >
              <span className="truncate">{label}</span>
              <ChevronDown className="h-3 w-3 shrink-0" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          Set the model and thinking
        </TooltipContent>
      </Tooltip>

      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        className={cn(MENU_CLASS, "w-80")}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {allModels ? (
          <>
            <button
              onClick={() => setAllModels(false)}
              className={cn(ROW_CLASS, "min-h-9 py-1.5 text-sm text-n-60")}
            >
              <ChevronLeft className="size-3.5 text-n-40" />
              <div className="flex-1">All models</div>
            </button>
            {models.map((option) => (
              <button
                key={option.id}
                disabled={option.disabled}
                onClick={() => {
                  setOpen(false);
                  setAllModels(false);
                  pickModel(option.id);
                }}
                className={cn(
                  ROW_CLASS,
                  "min-h-10 text-sm",
                  option.id === model && "bg-accent/60",
                )}
              >
                <ModelRowLabel label={option.label} />
                {option.id === model && (
                  <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                )}
              </button>
            ))}
            {control.modelSwitchLocked && (
              <p className="px-4 pt-2 pb-1 text-xs text-muted-foreground leading-snug">
                This chat is locked to the provider it started with. Start a new
                chat to use a different provider.
              </p>
            )}
          </>
        ) : (
          <>
            {aliases.map((option) => {
              const selected = option.model_id === model;
              const disabled = models.find(
                (m) => m.id === option.model_id,
              )?.disabled;
              return (
                <button
                  key={option.id}
                  disabled={disabled}
                  onClick={() => {
                    setOpen(false);
                    pickModel(option.model_id);
                  }}
                  className={cn(
                    ROW_CLASS,
                    "min-h-10 text-sm",
                    selected && "bg-accent/60",
                  )}
                >
                  <ModelRowLabel
                    label={option.label}
                    description={option.description}
                  />
                  {selected && (
                    <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                  )}
                </button>
              );
            })}
            {(hasMoreModels || !aliases.length) && (
              <button
                onClick={() => setAllModels(true)}
                className={cn(ROW_CLASS, "min-h-9 py-1.5 text-sm text-n-60")}
              >
                <div className="flex-1">More models</div>
                <ChevronRight className="size-3.5 text-n-40" />
              </button>
            )}
          </>
        )}

        {efforts.length > 0 && (
          <>
            <div className="-mx-2 my-2 h-px bg-border" />
            <ThinkingScale
              efforts={efforts}
              effort={effort}
              onSelect={pickEffort}
            />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** The model's efforts as a segmented scale, with the selected one's description under it. */
function ThinkingScale({
  efforts,
  effort,
  onSelect,
}: {
  efforts: HarnessCapabilityEffort[];
  effort: string | undefined;
  onSelect: (effort: string) => void;
}) {
  const description = efforts.find((e) => e.id === effort)?.description;
  return (
    <div className="px-2 pb-1">
      <div className="px-2 pt-2 pb-1 font-mono text-2xs font-medium uppercase tracking-[0.08em] text-n-50">
        Thinking
      </div>
      <div
        role="radiogroup"
        aria-label="Thinking"
        className="flex h-8 w-full items-center rounded-md bg-n-05 p-0.5 text-muted-foreground"
      >
        {efforts.map((option) => (
          <button
            key={option.id}
            role="radio"
            aria-checked={option.id === effort}
            data-state={option.id === effort ? "active" : "inactive"}
            onClick={() => onSelect(option.id)}
            className={cn(
              "inline-flex h-7 flex-1 items-center justify-center whitespace-nowrap rounded-sm px-1.5 text-xs font-medium transition-all",
              "hover:text-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-2xs dark:data-[state=active]:bg-n-20",
            )}
          >
            {EFFORT_SHORT[option.id] ?? option.label}
          </button>
        ))}
      </div>
      {description && (
        <div className="px-2 pt-1.5 text-xs text-muted-foreground leading-snug">
          {description}
        </div>
      )}
    </div>
  );
}
